import { supabase } from './supabase'

export const API = import.meta.env.VITE_API as string

export async function getJwt(): Promise<string> {
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token || ''
}

/** Appel authentifié vers l'API maison (/papi). Lève une erreur avec le message serveur en cas d'échec. */
export async function api<T = any>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const jwt = await getJwt()
  const headers: Record<string, string> = { Authorization: `Bearer ${jwt}`, ...(init.headers as Record<string, string> || {}) }
  let body = init.body
  if (init.json !== undefined) { headers['Content-Type'] = 'application/json'; body = JSON.stringify(init.json) }
  const r = await fetch(`${API}${path}`, { ...init, headers, body })
  const data = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(data.error || `Erreur ${r.status}`)
  return data as T
}

/** Téléversement multipart avec progression (XHR, car fetch ne rapporte pas l'upload). */
export function upload(path: string, files: File[], onProgress?: (pct: number) => void): Promise<any> {
  return new Promise(async (resolve, reject) => {
    const jwt = await getJwt()
    const fd = new FormData(); files.forEach(f => fd.append('file', f, f.name))
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${API}${path}`)
    xhr.setRequestHeader('Authorization', `Bearer ${jwt}`)
    xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round(e.loaded / e.total * 100)) }
    xhr.onload = () => {
      let d: any = {}; try { d = JSON.parse(xhr.responseText) } catch {}
      xhr.status < 300 ? resolve(d) : reject(new Error(d.error || `Erreur ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('Erreur réseau'))
    xhr.send(fd)
  })
}

export const fileSize = (b: number) => b > 1e9 ? (b / 1e9).toFixed(1) + ' Go' : b > 1e6 ? (b / 1e6).toFixed(1) + ' Mo' : Math.max(1, Math.round(b / 1e3)) + ' Ko'

export interface PortfolioItem {
  id: string
  title: string
  company: string
  description: string
  project_date: string | null
  video_url: string
  thumbnail_url: string
  published: boolean
  sort_order: number
}

/** Date « septembre 2026 » à partir de YYYY-MM-DD. */
export function fmtMonth(d: string | null) {
  if (!d) return ''
  const [y, m] = d.split('-').map(Number)
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString('fr-CA', { month: 'long', year: 'numeric' })
}

/** Détecte YouTube / Vimeo et retourne l'URL d'embed ; sinon null (= fichier vidéo direct). */
export function embedUrl(u: string): string | null {
  if (!u) return null
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/)
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}?autoplay=1&rel=0`
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`
  return null
}
